import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { supabase } from '../lib/supabase';

interface User {
  id: string;
  email: string;
  user_metadata: {
    name: string;
    role: 'administrador' | 'usuario_avanzado' | 'usuario_estandar';
    grupo?: {
      id: string;
      nombre: string;
      role: string;
    };
  };
}

interface AuthState {
  user: User | null;
  isLoading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  checkAuth: () => Promise<void>;
  refreshUserData: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isLoading: true,

      signIn: async (email: string, password: string) => {
        try {
          set({ isLoading: true });
          
          // Clear any existing session first to prevent conflicts
          await supabase.auth.signOut();
          
          // Clear local storage items
          localStorage.removeItem('supabase.auth.token');
          localStorage.removeItem('auth-storage');
          
          // Attempt to sign in
          const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
            email,
            password,
          });
          
          if (authError) {
            // If email not confirmed, throw specific error
            if (authError.message.includes('Email not confirmed')) {
              throw new Error('Por favor confirma tu email antes de iniciar sesión. Revisa tu bandeja de entrada.');
            }
            throw authError;
          }

          if (!authData.user) throw new Error('No se pudo iniciar sesión');

          // Get user data from usuarios table
          const { data: userData, error: userError } = await supabase
            .from('usuarios')
            .select(`
              id,
              email,
              nombre,
              role,
              estado,
              grupo:grupos_usuarios(id, nombre, role)
            `)
            .eq('id', authData.user.id)
            .single();

          if (userError) throw userError;
          if (!userData) throw new Error('Usuario no encontrado en la base de datos');
          
          // Check if user is active
          if (userData.estado !== 'activo') {
            await supabase.auth.signOut();
            localStorage.removeItem('supabase.auth.token');
            localStorage.removeItem('auth-storage');
            throw new Error('Usuario inactivo. Contacte al administrador.');
          }

          set({ 
            user: {
              ...authData.user,
              user_metadata: {
                name: userData.nombre,
                role: userData.role,
                grupo: userData.grupo
              }
            },
            isLoading: false
          });
        } catch (error: any) {
          console.error('Error en signIn:', error);
          set({ isLoading: false });
          throw error;
        }
      },

      signOut: async () => {
        try {
          set({ isLoading: true });
          
          // Clear local storage to ensure all auth data is removed
          localStorage.removeItem('auth-storage');
          localStorage.removeItem('supabase.auth.token');
          
          const { error } = await supabase.auth.signOut();
          if (error) throw error;
          
          set({ user: null, isLoading: false });
        } catch (error) {
          console.error('Error en signOut:', error);
          // Still clear user state on error
          set({ user: null, isLoading: false });
          localStorage.removeItem('auth-storage');
          localStorage.removeItem('supabase.auth.token');
        }
      },

      checkAuth: async () => {
        try {
          set({ isLoading: true });
          
          // Get current session
          const { data: { session }, error: sessionError } = await supabase.auth.getSession();
          
          if (sessionError) {
            console.error('Session error:', sessionError);
            
            // Clear local storage and set user to null
            localStorage.removeItem('supabase.auth.token');
            localStorage.removeItem('auth-storage');
            set({ user: null, isLoading: false });
            return;
          }
          
          if (!session) {
            // No valid session, clear local storage and set user to null
            localStorage.removeItem('supabase.auth.token');
            localStorage.removeItem('auth-storage');
            set({ user: null, isLoading: false });
            return;
          }

          // Get user data
          const { data: { user }, error: userError } = await supabase.auth.getUser();
          
          if (userError) {
            console.error('User error:', userError);
            
            // Clear local storage and set user to null
            localStorage.removeItem('supabase.auth.token');
            localStorage.removeItem('auth-storage');
            set({ user: null, isLoading: false });
            return;
          }
          
          if (!user) {
            // No valid user, clear local storage and set user to null
            localStorage.removeItem('supabase.auth.token');
            localStorage.removeItem('auth-storage');
            set({ user: null, isLoading: false });
            return;
          }

          // Get additional user data from database
          const { data: userData, error: dbError } = await supabase
            .from('usuarios')
            .select(`
              id,
              email,
              nombre,
              apellidos,
              departamento_id,
              role,
              estado,
              grupo_id,
              grupo:grupos_usuarios(id, nombre, role)
            `)
            .eq('id', user.id)
            .single();

          if (dbError) {
            console.error('Error al obtener datos del usuario:', dbError);
            // If we can't get user data, redirect to login
            await supabase.auth.signOut();
            localStorage.removeItem('supabase.auth.token');
            localStorage.removeItem('auth-storage');
            set({ user: null, isLoading: false });
            return;
          }

          if (!userData) {
            console.error('No se encontraron datos del usuario');
            await supabase.auth.signOut();
            localStorage.removeItem('supabase.auth.token');
            localStorage.removeItem('auth-storage');
            set({ user: null, isLoading: false });
            return;
          }

          // Check if user is active
          if (userData.estado !== 'activo') {
            await supabase.auth.signOut();
            localStorage.removeItem('supabase.auth.token');
            localStorage.removeItem('auth-storage');
            set({ user: null, isLoading: false });
            return;
          }

          set({ 
            user: {
              ...user,
              user_metadata: {
                name: userData.nombre,
                role: userData.role,
                grupo: userData.grupo
              }
            },
            isLoading: false 
          });
        } catch (error: any) {
          console.error('Error checking auth:', error);
          
          // Handle refresh token errors specifically
          if (error instanceof Error && 
              (error.message.includes('refresh_token_not_found') || 
               error.message.includes('Invalid Refresh Token'))) {
            console.log('Refresh token error detected, clearing auth state');
            await supabase.auth.signOut();
            localStorage.removeItem('supabase.auth.token');
            localStorage.removeItem('auth-storage');
          }
          
          // Set user to null and stop loading
          set({ user: null, isLoading: false });
        }
      },

      refreshUserData: async () => {
        try {
          const { data: { user }, error: userError } = await supabase.auth.getUser();
          
          if (userError) {
            if (userError.message.includes('refresh_token_not_found') || 
                userError.message.includes('Invalid Refresh Token')) {
              // Clear local storage and throw error
              localStorage.removeItem('supabase.auth.token');
              localStorage.removeItem('auth-storage');
            }
            throw userError;
          }
          
          if (!user) {
            throw new Error('No hay usuario autenticado');
          }

          const { data: userData, error: dbError } = await supabase
            .from('usuarios')
            .select(`
              id,
              email,
              nombre,
              apellidos,
              departamento_id,
              role,
              estado,
              grupo_id,
              grupo:grupos_usuarios(id, nombre, role)
            `)
            .eq('id', user.id)
            .single();

          if (dbError) throw dbError;
          if (!userData) throw new Error('Usuario no encontrado');
          
          // Check if user is active
          if (userData.estado !== 'activo') {
            throw new Error('Usuario inactivo');
          }

          set({ 
            user: {
              ...user,
              user_metadata: {
                name: userData.nombre,
                role: userData.role,
                grupo: userData.grupo
              }
            }
          });
        } catch (error) {
          console.error('Error refreshing user data:', error);
          throw error;
        }
      }
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ user: state.user }),
      storage: typeof window !== 'undefined' ? window.localStorage : undefined
    }
  )
);